<template>
  <div class="zoox-component zoox-main-layout">
    <!--Evento emitido ao clicar em uma opção do menu
          @event click
          @property {Object} menuOption - Valor da opção selecionada
       -->
    <v-main
      id="zoox-content"
      :style="{
        'background-color': background,
      }"
      class="zoox-content"
    >
      <z-header
        with-logo
        :color="headerColor"
        :class="{'fixed-header': fixedHeader}"
        :dark="dark"
        :lang="lang"
        :lang-options="langOptions"
        :product="title"
        @change-lang="onChangeLang"
      >
        <template #user-menu>
          <z-user-menu-options
            :items="userMenuItems"
            :user-name="userName"
            :user-email="userEmail"
            :src-avatar="userAvatar"
            @click:item="$emit('click:user-menu-item', $event)"
            @logout="onClickLogout"
          ></z-user-menu-options>
        </template>

        <template #product>
          <slot name="product">
            <v-img
              class="logo-menu-bar"
              contain
              max-height="35"
              max-width="35"
              :src="logo || require('../../../../assets/img/logo_black.svg')"
              @click="onClickLogo"
            ></v-img>

            <div
              class="d-flex flex-column justify-center"
              :style="{
                color: dark ? 'white' : 'var(--black-800)',
              }"
            >
              <z-breadcrumbs
                class="custom-breadcrumbs pa-0"
                :items="breadcrumb"
                @click="onClickBreadcrumb"
              >
                <template #divider>
                  <z-icon>$arrow_right_alt_rounded</z-icon>
                </template>
              </z-breadcrumbs>

              <div class="d-flex align-center">
                <v-toolbar-title>
                  <h3 style="line-height: 20px">
                    {{ title }}
                  </h3>
                </v-toolbar-title>

                <z-icon
                  v-if="!hideFavorite"
                  size="21"
                  class="ml-1"
                  :color="isFavorite ? 'var(--yellow)' : ''"
                  @click="$emit('favorite:page')"
                >
                  {{ isFavorite ? '$star_rounded' : '$star_border_rounded' }}
                </z-icon>
              </div>
            </div>
          </slot>
        </template>

        <slot name="append-actions"></slot>

        <z-options-menu
          v-if="menuOptions && menuOptions.length > 0"
          :title="menuTitle"
          :options="menuOptions"
          @click:option="$emit('click:option', $event)"
          @favorite:option="$emit('favorite:option', $event)"
        ></z-options-menu>

        <slot name="actions"> </slot>
      </z-header>

      <slot name="content"></slot>
    </v-main>
  </div>
</template>

<script>
/**
 * <codesandbox-link link="https://codesandbox.io/s/zmainlayout-exemplo-pb6uz"></codesandbox-link>
 *
 * Componente de fachada que agrega z-header, z-nav-drawer e adiciona um slot para o conteúdo
 *
 * @displayName Main Layout
 */
export default {
  name: 'ZMenuBarLayout',

  props: {
    /**
     * Nome do produto que está sendo desenvolvido
     */
    title: {
      type: String,
      required: true,
      default: 'DEFAULT TITLE',
    },
    /**
     * Linguagens disponíveis no select. Utilizar somente
     * en-pt-es.
     */
    langOptions: {
      type: Array,
      required: false,
      default: () => ['pt', 'en'],
    },
    /**
     * Define as opções do menu
     *
     */
    menuOptions: {
      type: Array,
      required: false,
      default: () => [],
    },

    /**
     * Define o título do menu
     */
    menuTitle: {
      type: String,
      required: false,
      default: '',
    },
    /**
     * Nome do usuário logado para colocar no header
     */
    userName: {
      type: String,
      required: true,
      default: '',
    },
    /**
     * Linguagem selecionada
     */
    lang: {
      type: String,
      required: false,
    },
    /**
     * Caminho da imagem, caso queria adicionar um avatar
     */
    userAvatar: {
      type: [Object, String],
      required: false,
      default: '',
    },

    /**
     * Flag para esconder a logo do navigation drawer
     */
    hideNavLogo: {
      type: Boolean,
      default: false,
    },

    /**
     * Email do usuário
     */
    userEmail: {
      type: String,
      default: 'Zoox Smart Data',
    },
    /**
     * Cor do background do conteúdo do layout
     */
    background: {
      type: String,
      default: 'var(--background)',
    },
    /**
     * Flag para indicar que o fundo é dark. Usa textos em branco
     */
    dark: {
      type: Boolean,
      default: false,
    },

    breadcrumb: {
      type: Array,
      default: () => [],
    },

    /**
     * Flag para indicar se a página é favorita
     */
    isFavorite: {
      type: Boolean,
      default: false,
    },

    /**
     * Caminho da imagem da logo
     */
    logo: {
      type: String,
      default: '',
    },

    /**
     * Itens do menu de usuário
     */
    userMenuItems: {
      type: Array,
      default: () => [],
    },

    /**
     * Flag para deixar o header fixo
     */
    fixedHeader: {
      type: Boolean,
      default: false,
    },

    /**
     * Define a cor do header
     */
    headerColor: {
      type: String,
      default: 'var(--background)',
    },

    /**
     * Flag para esconder o botão de favoritar
     */
    hideFavorite: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {}
  },
  methods: {
    onChangeLang(lang) {
      /**
       * Evento emitido para alterar a linguagem
       *
       * @property {String} lang - Linguagem selecionada
       */
      this.$emit('change:lang', lang)
    },
    onClickLogout() {
      /**
       * Evento emitido para fazer logout da aplicação
       */
      this.$emit('logout')
    },
    onClickLogo() {
      /**
       * Evento disparado quando usuário clica na logo do menu
       */
      this.$emit('click:logo')
    },

    onClickBreadcrumb(item) {
      if (item.disabled) return
      /**
       * Evento disparado quando usuário clica no breadcrumb
       */
      this.$emit('click:breadcrumb', item)
    },
  },
  i18n: {
    messages: {
      pt: {
        portuguese: 'Português',
        english: 'Inglês',
        spanish: 'Espanhol',
        logout: 'Logout',
      },
      es: {
        portuguese: 'Portugués',
        english: 'Inglés',
        spanish: 'Español',
        logout: 'Logout',
      },
      en: {
        portuguese: 'Portuguese',
        english: 'English',
        spanish: 'Spanish',
        logout: 'Logout',
      },
      fr: {
        portuguese: 'Portugais',
        english: 'Anglais',
        spanish: 'Espagnol',
        logout: 'Déconnexion',
      },
      tr: {
        portuguese: 'Portekizce',
        english: 'İngilizce',
        spanish: 'İspanyolca',
        logout: 'Çıkış',
      },
    },
  },
}
</script>

<style lang="scss" scoped>
.zoox-main-layout {
  position: relative;
  display: flex;
  height: 100%;
}

.zoox-content {
  height: 100%;
  position: relative;

  .fixed-header {
    position: sticky;
    top: 0;
    z-index: 1;
  }

  :deep(.v-toolbar__content) {
    gap: 15px;
  }

  :deep(.zoox-header > div) {
    padding: 0px 20px !important;
  }

  :deep(.zoox-header > div > *) {
    margin-right: 0px !important;
    margin-left: 0px !important;
  }

  :deep(.lang button) {
    padding: 0px;
  }
}

.logo-menu-bar {
  cursor: pointer;
}

.custom-breadcrumbs {
  ::v-deep .zoox-breadcrumbs-text {
    font-size: 11px !important;
    text-decoration: underline !important;
    font-weight: 400;

    &.disabled {
      text-decoration: none !important;
    }
  }

  ::v-deep .v-breadcrumbs__divider {
    padding: 0 5px !important;
  }
}
</style>
